/* eslint-disable no-restricted-globals */
import { useMemo } from 'react';

import useForm from '../utils/useForm';
import LenderService from '../../services/LenderService';
import { formatCurrency } from '../../helpers/currencyMask';

const yesNoOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const useLoanForm = (onSubmit, values, readOnly, currentTab) => {
  const fields = useMemo(
    () => [
      {
        title: 'Lender',
        isHidden: () => currentTab !== 'Note',
        fields: readOnly
          ? [
              {
                title: '',
                name: '_lender.name',
                type: 'text',
                readOnly: true,
              },
            ]
          : [
              {
                title: '',
                name: 'lender_id',
                type: 'fetchableSelect',
                fetchOptions: {
                  key: ['lenders'],
                  queryFn: LenderService.getLenders,
                  labelKey: 'name',
                },
                required: true,
                readOnly:
                  (values?.lien_holder_id !== 0 &&
                    values?.lien_holder_id !== undefined &&
                    values?.lien_holder_recording_number !== '') ||
                  (values?.lien_holder_recording_number !== undefined &&
                    values?.lien_holder_recording_number !== '') ||
                  readOnly,
              },
            ],
      },
      {
        title: 'Loan Information',
        isHidden: () => currentTab !== 'Note',
        fields: [
          {
            title: 'Lender Loan Number',
            name: 'loan_number',
            type: readOnly ? 'editableText' : 'text',
            required: false,
            readOnly,
          },
          {
            title: 'Lien Priority',
            name: 'lien_priority',
            type: 'select',
            required: true,
            readOnly,
            options: [
              { value: '1', label: '1' },
              { value: '2', label: '2' },
              {
                value: '3',
                label: '3',
              },
            ],
          },
          {
            title: 'Close Date',
            name: 'close_date',
            type: 'date',
            required: true,
            readOnly,
          },
          {
            title: 'First Payment Date',
            name: 'first_payment_date',
            type: 'date',
            required: true,
            readOnly,
          },
          {
            title: 'Maturity Date',
            name: 'maturity_date',
            type: 'date',
            required: true,
            readOnly,
          },
          {
            title: 'Paid Off Date',
            name: 'paid_off_date',
            format: readOnly && !values?.paid_off_date ? () => 'n/a' : null,
            type: readOnly && !values?.paid_off_date ? 'text' : 'date',
            readOnly,
          },
          {
            title: 'Payment Due Day',
            name: 'payment_due_day',
            type: 'number',
            required: true,
            readOnly,
          },
          {
            title: 'Term (Months)',
            name: 'term',
            type: 'number',
            required: true,
            customValidation: [
              'is-within-range',
              'Term must be between 1 and 360',
              (value) => value >= 1 && value <= 360,
            ],
            readOnly,
          },
        ],
      },
      {
        title: 'Loan Terms',
        isHidden: () => currentTab !== 'Note',
        fields: [
          {
            title: 'Rate Type',
            name: 'rate_type',
            type: 'select',
            options: [
              { value: 'fixed', label: 'Fixed' },
              { value: 'adjustable', label: 'Adjustable' },
            ],
            required: true,
            readOnly,
          },
          {
            title: 'Amortization Type',
            name: 'amortization_type',
            type: 'select',
            options: [
              { value: 'fully', label: 'Fully' },
              { value: 'interest only', label: 'Interest only' },
            ],
            required: true,
            readOnly,
          },
          {
            title: 'Loan Purpose',
            name: 'loan_purpose',
            type: 'select',
            options: [
              { value: 'Purchase', label: 'Purchase' },
              {
                value: 'Refinance - Rate Term',
                label: 'Refinance - Rate Term',
              },
              { value: 'Refinance - Cash Out', label: 'Refinance - Cash Out' },
              { value: 'Construction', label: 'Construction' },
            ],
            readOnly,
          },
          {
            title: 'Starting Principal',
            name: 'original_principal',
            type: 'currency',
            required: true,
            readOnly,
          },
          {
            title: 'Principal Paid',
            name: '_principal_paid.total',
            type: 'currency',
            readOnly: true,
          },
          {
            title: 'Remaining Principal',
            name: 'doesntmatter',
            type: 'text',
            format: (vals) => {
              let originalPrincipal = vals.original_principal;

              if (originalPrincipal) {
                originalPrincipal = `${originalPrincipal}`.replace(/,/g, '');
              }

              const originalPrincipalCents = Math.round(
                originalPrincipal * 100
              );
              const totalPaidCents = Math.round(
                (vals?._principal_paid?.total || 0) * 100
              );

              // Perform the subtraction in cents
              const remainingPrincipalCents =
                originalPrincipalCents - totalPaidCents;

              // Convert back to dollars for display and format to two decimal places
              const remainingPrincipal = (
                remainingPrincipalCents / 100
              ).toFixed(2);

              return `$${formatCurrency(remainingPrincipal)}`;
            },
            readOnly: true,
          },
          {
            title: 'Rate',
            name: 'rate',
            type: 'percentage',
            /*  link: readOnly
              ? null
              : {
                  title: 'Rate History',
                  url: '#',
                }, */
            required: true,
            readOnly,
          },
          {
            title: 'Balloon Term',
            name: 'balloon_term',
            type: readOnly && !values?.balloon_term ? 'text' : 'number',
            format: readOnly && !values?.balloon_term ? () => 'n/a' : null,
            readOnly,
          },
          {
            title: 'IO Intro Period',
            name: 'interest_only_intro_term',
            type:
              readOnly && !values?.interest_only_intro_term ? 'text' : 'number',
            format:
              readOnly && !values?.interest_only_intro_term
                ? () => 'n/a'
                : null,
            readOnly,
          },
          {
            title: 'Interest Deferred',
            name: 'interest_deferred',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            readOnly,
          },
          {
            title: 'Servicing Fee Payment',
            name: 'servicing_fee',
            type: 'currency',
            readOnly,
          },
          {
            title: 'Monthly PI Payment',
            name: 'pi_payment',
            type: 'currency',
            readOnly,
          },
        ],
      },
      {
        title: 'Steps',
        isHidden: (vals) => {
          return !vals.scheduled_rate_changes;
        },
        fields: [
          {
            type: 'numberPercentageArray',
            name: 'scheduled_rate_changes',
            validation: 'object',
            noInitialValue: true,
            readOnly,
            innerFields: [
              {
                title: 'Change Month',
                name: 'change_month',
                type: 'number',
                readOnly,
              },
              {
                title: 'Rate',
                name: 'rate',
                type: 'percentage',
                readOnly,
              },
            ],
          },
        ],
      },
      {
        title: 'Impounds',
        isHidden: () => currentTab !== 'Impounds',
        fields: [
          {
            title: 'Tax Impound',
            name: 'tax_impound',
            type: readOnly ? 'text' : 'radio',
            format: readOnly
              ? (vals) => (vals?.tax_impound ? 'Yes' : 'No')
              : null,
            options: yesNoOptions,
            defaultValue: true,
            readOnly,
          },
          {
            title: 'Insurance Impound',
            name: 'insurance_impound',
            type: readOnly ? 'text' : 'radio',
            format: readOnly
              ? (vals) => (vals?.insurance_impound ? 'Yes' : 'No')
              : null,
            options: yesNoOptions,
            defaultValue: true,
            readOnly,
          },
          {
            title: 'HOA Impound',
            name: 'hoa_impound',
            type: readOnly ? 'text' : 'radio',
            format: readOnly
              ? (vals) => (vals?.hoa_impound ? 'Yes' : 'No')
              : null,
            options: yesNoOptions,
            defaultValue: true,
            readOnly,
          },
          {
            title: 'Monthly Tax Impound',
            name: 'impound_tax',
            type: 'currency',
            readOnly,
          },
          {
            title: 'Monthly Insurance Impound',
            name: 'impound_insurance',
            type: 'currency',
            readOnly,
          },
          {
            title: 'Monthly HOA Impound',
            name: 'impound_hoa',
            type: 'currency',
            readOnly,
          },
        ],
      },
      {
        title: 'Adjustable Rate Terms',
        isHidden: (vals) =>
          vals.rate_type !== 'adjustable' || currentTab !== 'Note',
        fields: [
          {
            title: 'Index',
            name: 'arm_index',
            type: 'select',
            options: [{ value: 'WSJP', label: 'WSJP' }],
            readOnly,
          },
          {
            title: 'Margin',
            name: 'arm_margin',
            type: 'percentage',
            readOnly,
          },
          {
            title: 'Ceiling',
            name: 'arm_ceiling',
            type: 'percentage',
            readOnly,
          },
          {
            title: 'Floor',
            name: 'arm_floor',
            type: 'percentage',
            readOnly,
          },
          {
            title: 'First Change Term',
            name: 'arm_first_change_term',
            type: 'number',
            readOnly,
          },
          {
            title: 'First Change Cap',
            name: 'arm_first_change_cap',
            type: 'percentage',
            readOnly,
          },
          {
            title: 'Sub Change Term',
            name: 'arm_sub_change_term',
            type: 'number',
            readOnly,
          },
          {
            title: 'Sub Change Cap',
            name: 'arm_sub_change_cap',
            type: 'percentage',
            readOnly,
          },
        ],
      },
      {
        title: 'Penalties',
        isHidden: () => currentTab !== 'Note',
        fields: [
          {
            title: 'Grace Period Days',
            name: 'grace_period_days',
            type: 'number',
            required: true,
            readOnly,
          },
          {
            title: 'Late Penalty Percent',
            name: 'late_penalty_percent',
            type: 'percentage',
            required: true,
            readOnly,
          },
          {
            title: 'Minimum Late Charge',
            name: 'late_penalty_minimum',
            type: 'currency',
            readOnly,
          },
          {
            title: 'Late additional daily amount',
            name: 'late_additional_daily_amount',
            type: 'number',
            readOnly,
          },
        ],
      },
      {
        title: 'DOT',
        isHidden: () => currentTab !== 'DOT',
        fields: [
          {
            title: 'Trustee',
            name: 'trustee',
            type: 'text',
            readOnly,
          },
          {
            title: 'DOT Recording Number',
            name: 'recording_number',
            type: 'text',
            readOnly,
          },
          {
            title: 'DOT Recording Date',
            name: 'recording_date',
            format: readOnly && !values?.recording_date ? () => 'n/a' : null,
            type: readOnly && !values?.recording_date ? 'text' : 'date',
            readOnly,
          },
        ],
      },
    ],
    [
      readOnly,
      values?.lien_holder_id,
      values?.lien_holder_recording_number,
      values?.paid_off_date,
      values?.recording_date,
      values?.balloon_term,
      values?.interest_only_intro_term,
      currentTab,
    ]
  );

  return useForm({ fields, onSubmit, values });
};

export default useLoanForm;
